import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NavController, LoadingController, MenuController, PopoverController } from '@ionic/angular';
import { Router, NavigationExtras } from '@angular/router';

import { ImageData } from '../../models/image-data';
import { Constants } from '../../models/constants';

import { Alert } from '../../services/alert';
import { UserService } from '../../services/user-service';
import { Util } from '../../services/util';
import { ProfPicService } from '../../services/prof-pic-service';
import { RoomTasks } from '../../services/room-tasks'
import { PromoService, PromoActivation } from 'src/app/services/promo-service';
import { EventSeriesInfo } from 'src/app/models/event-series-info';
import { GiftCard, GiftCardsService } from 'src/app/services/gift-cards-service';
import { VibeToast } from 'src/app/services/vibe-toast';

import { EnterAddressPage } from '../enter-address/enter-address.page';
import { AnalyticsService } from 'src/app/services/analytics-service';
import { MetaTagService } from 'src/app/services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';

interface AccountUser {
  imgURL?: string;
  fullName?: string;
  email?: string;
  phone?: string;
}

@Component({
  selector: 'app-account',
  templateUrl: './account.page.html',
  styleUrls: ['./account.page.scss'],
})
export class AccountPage implements OnInit {

  accountUser: AccountUser = {};
  paymentCardString: string = '';
  paymentAdded = false
  isMenuOpen = false

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private profPicService: ProfPicService,
    private alert: Alert,
    private observables: ObservableService,
    private router: Router,
    private changeRef: ChangeDetectorRef,
    private promoService: PromoService,
    private popoverCtrl: PopoverController,
    private analytics: AnalyticsService,
    public userService: UserService,
    public toast: VibeToast,
    private menuCtrl: MenuController,
    private metaTagService: MetaTagService
  ) {

    const user = this.userService.user

    if (!user) {
      const sub = this.observables.userFetched.subscribe(() => {
        sub.unsubscribe()
        let eventualLoad = true
        this.loadUIContent(eventualLoad)
      });
      return
    }

    this.loadUIContent()
  }

  ngOnInit() { }

  loadUIContent(eventualLoad?: boolean) {
    this.accountUser = {
      fullName: this.userService.user.fullName,
      email: this.userService.user.email,
      phone: this.userService.user.nationalPhoneNumber,
    }

    if (this.userService.user.profPicURL) {
      this.accountUser.imgURL = this.userService.user.profPicURL;
    } else {
      this.accountUser.imgURL = "assets/img/profile-placeholder.png";
    }

    let paymentAdded = Util.isDefined(this.userService.user.customer) && Util.isDefined(this.userService.user.customer.last4);
    this.paymentCardString = 'No payment added';
    if (paymentAdded) {
      this.paymentCardString = `Card ending ${this.userService.user.customer.last4}`;
      if (this.userService.user.customer.cardBrand) {
        this.paymentCardString = `${this.userService.user.customer.cardBrand} ending *${this.userService.user.customer.last4}`;
      }
    }

    if (eventualLoad) {
      this.changeRef.detectChanges()
    }
  }

  ionViewWillEnter() {
    this.metaTagService.updateTitle('Account')
    this.metaTagService.updateTags({
      facebookURL: 'https://app.getshortcut.co/account',
      keywords: 'account settings, profile settings, account, profile',
      facebookTitle: 'Account | Shortcut',
      twitterTitle: 'Account | Shortcut',
      description: 'Access and update your Shortcut account settings.',
      facebookDescription: 'Access and update your Shortcut account settings.',
      twitterDescription: 'Access and update your Shortcut account settings.',
    })

    this.menuCtrl.enable(true);
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/account')
  }

  async saveEmail() {

    let email = this.accountUser.email.trim();

    if (email == this.userService.user.email) {
      return
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    let newParams: any = {
      email: email,
    }

    this.userService.update(newParams,
      () => {
        loading.dismiss()
        this.loadUIContent()
        this.changeRef.detectChanges()
      },
      (msg: string) => {
        loading.dismiss()
        this.error(msg)
      });
  }

  updatePayment() {
    this.navCtrl.navigateForward('/payment')
  }

  private error(msg: string) {
    this.alert.show("Error", msg);
  }

  uploadPicPressed() {

    let showActionSheet = true

    this.profPicService.uploadPhoto(showActionSheet, (data: ImageData) => {
      this.userService.saveProfPicToParse(data, () => {

        this.accountUser.imgURL = this.userService.user.profPicURL;
        this.observables.publishProfPicUpdated()
        this.changeRef.detectChanges()

      }, (errorMsg: string) => {
        this.error(errorMsg)
      });

    }, (errorMsg) => {
      this.alert.show('Error', errorMsg, 'Ok');
    });
  }

  enterPromoCode() {
    this.alert.showPrompt('Enter Promo Code', null, 'promo', 'CODE', 'text', 'Submit', (data) => {
      let promo = data.promo;
      this.promoService.checkPromoValidity(promo, (barberID?: string, barberLink?: string, activation?: PromoActivation, eventRedirectionData?: EventSeriesInfo[], giftCardData?: GiftCard) => {

        if (activation) {
          this.promoService.displayPromoActivation(activation)
        } else if (eventRedirectionData) {
          this.handleMobileEventCodeSuccess(promo, eventRedirectionData)
        } else if (giftCardData) {
          this.alert.show('Redeem gift card?', `Code: ${giftCardData.hyphenatedCode}`, 'Redeem', async () => {

            let loading = await this.loadingCtrl.create()
            loading.present()

            GiftCardsService.redeemGiftCard(giftCardData.code, (giftCard: GiftCard) => {
              loading.dismiss()
              this.alert.show('Success!', `${Util.formatCost(giftCard.amount, this.userService.user.currency)} has been credited to your account and will be applied automatically to your next order.`);
              this.observables.publishUpdateUser()
            }, (errorMsg: string) => {
              loading.dismiss()
              this.alert.show('Error', errorMsg)
            })
          }, 'Cancel', () => { });
        } else {
          this.promoService.storeCoupon(promo);
          this.toast.showPromoToast(promo);

          if (barberID && barberLink) {
            const navigationExtras: NavigationExtras = {
              state: {
                barberID,
                barberProfileNavHistory: 'Coupon'
              }
            }
            this.navCtrl.navigateForward(`/pro/${barberLink}`, navigationExtras);
          }
        }

      }, (errorMsg) => {
        this.toast.show(`Promo invalid. ${errorMsg}`);
      });
    }, 'Cancel', () => { }, 'SC-caps');
  }

  logOut() {

    this.alert.show("Log out", "Are you sure you want to sign out?", "Log out", () => {
      this.observables.publishLogOut()
    }, "Cancel", () => { })
  }

  openPage(url: string) {
    window.open(url, '_blank')
  }

  async adminChat() {
    this.observables.publishOpenNewAdminChat();
  }

  async resetPassword() {

    let loading = await this.loadingCtrl.create()
    loading.present()

    this.userService.resetPassword(this.userService.user.email, () => {
      loading.dismiss();
      this.alert.show('Reset Password', 'Email sent to reset your password.')
    }, (errMsg: string) => {
      loading.dismiss();
      console.error(errMsg)
    })

  }

  private goToChat(roomID: string) {
    this.observables.publishOpenNewChat(roomID)
  }

  emailFromBrowser() {
    let helpEmail = 'support@getshortcut.co'
    let subject = 'Contact Shortcut'
    let body = ''
    window.location.href = 'mailto:' + helpEmail + '?subject=' + subject + '&body=' + body
  }

  async openAddress(event?: any) {

    const addressModal = await this.popoverCtrl.create({
      component: EnterAddressPage,
      componentProps: {},
      event: event,
      cssClass: ['SCWEB-popover', 'SCWEB-popover-address']
    })

    addressModal.onDidDismiss().then((val: any) => {
      if (!val || !val.data) {
        return
      }

      if (val.data.address) {

        this.analytics.track(AnalyticsService.EnteredAddress_Account)

        this.userService.update({
          address: val.data.address
        }, () => {
          this.userService.fetch(() => {
            this.changeRef.detectChanges()
          })
        }, (errorMsg) => { });
      }
    });

    addressModal.present()
  }

  private handleMobileEventCodeSuccess(code: string, eventSeriesArray: EventSeriesInfo[]) {

    this.alert.show('You entered an event code.', 'Please contact Shortcut support for help getting set up with the event.', 'Ok')

    // TODO: Handle mobile event code. This is can be called from entering a mobile event code as promo by accident.

    // if (eventSeriesArray.length === 1) {

    //   this.eventService.pullPublicEventInfo(eventSeriesArray[0].id, (eventInfo: PublicEventInfo) => {
    //     const eventID = eventSeriesArray[0].id
    //     let navigationExtras: NavigationExtras = {
    //       state: {
    //         publicEventInfo: eventInfo,
    //         eventID
    //       }
    //     }
    //     this.navCtrl.navigateForward(`/event/${eventID}`, navigationExtras)

    //   }, (errorMsg: string) => {
    //     this.toast.show(`Code invalid. ${errorMsg}`);
    //   });

    // } else if (eventSeriesArray.length > 0) {

    //   let navigationExtras: NavigationExtras = {
    //     state: {
    //       mobileEventCode: code,
    //       eventSeries: eventSeriesArray
    //     }
    //   }
    //   this.navCtrl.navigateForward('/event-series', navigationExtras)

    // } else {
    //   this.toast.show(`Code invalid.`);
    // }

  }
}
